import React, { useEffect, useState } from 'react';
import { Tag, Trash2 } from 'lucide-react';
import localApiAdapter from '../../core/services/localApiAdapter';
import { generateEntityReference } from '../../shared/utils/generateEntityReference';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';
import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import { isDateInRange } from '../../products/saudi/utils/dateRangePresets';
import { getInventoryEntityPrefix } from './inventaireConfigDefaults';
import {
  sliceQawaemStats,
  QAWAEM_KPI_GRID_CLASS,
} from '../../products/saudi/components/QawaemErpListPageShell';

const TableCheckbox = QawaemTableCheckbox;

export { sliceQawaemStats, QAWAEM_KPI_GRID_CLASS };

/** Create/edit modal field grid — 3 columns on md+ (aligned with ACHATS_FORM_GRID). */
export const INVENTAIRE_FORM_GRID = 'grid grid-cols-1 md:grid-cols-3 gap-4';

/** Normalize list payloads from localApiAdapter ({ data } or bare array). */
export function unwrapInventaireApiList(response) {
  const data = response?.data ?? response;
  return Array.isArray(data) ? data : [];
}

/** Live inventory warehouses for inventaire dialog pickers. */
export async function fetchInventaireWarehouses() {
  try {
    const response = await localApiAdapter.get('/inventory/warehouses');
    return unwrapInventaireApiList(response);
  } catch {
    return [];
  }
}

/** Live CRM/facturation suppliers for inventaire dialogs. */
export async function fetchInventaireSuppliers() {
  try {
    const response = await localApiAdapter.get('/facturation/fournisseurs');
    return unwrapInventaireApiList(response);
  } catch {
    return [];
  }
}

/** Live CRM/facturation clients for inventaire dialogs. */
export async function fetchInventaireClients() {
  try {
    const response = await localApiAdapter.get('/facturation/clients');
    return unwrapInventaireApiList(response);
  } catch {
    return [];
  }
}

/** Standard warehouse select options (id + name/code label). */
export function buildInventaireWarehouseSelectOptions(warehouses = []) {
  return warehouses.map((wh) => ({
    value: wh.id,
    label: wh.code ? `${wh.name} (${wh.code})` : wh.name || wh.id,
  }));
}

/** Standard product select options from catalog products. */
export function buildInventaireProductSelectOptions(products = []) {
  return products.map((prod) => ({
    value: prod.id,
    label: prod.reference ? `${prod.name} (${prod.reference})` : prod.name || prod.id,
  }));
}

/** Resolve warehouse_id + warehouse_name after picker change. */
export function mapInventaireWarehouseSelection(warehouseId, warehouses = []) {
  const wh = warehouses.find((w) => String(w.id) === String(warehouseId));
  return {
    warehouse_id: warehouseId || '',
    warehouse_name: wh?.name || '',
  };
}

/** Supplier label for pickers (company_name or name). */
export function inventaireSupplierLabel(s) {
  return s?.company_name || s?.name || '';
}

/** Standard supplier select options for inventaire dialogs. */
export function buildInventaireSupplierSelectOptions(suppliers = []) {
  return suppliers.map((s) => ({
    value: s.id,
    label: inventaireSupplierLabel(s) || s.id,
  }));
}

/** Resolve supplier_id + supplier_name after picker change. */
export function mapInventaireSupplierSelection(supplierId, suppliers = []) {
  const s = suppliers.find((x) => String(x.id) === String(supplierId));
  return {
    supplier_id: supplierId || '',
    supplier_name: s ? inventaireSupplierLabel(s) : '',
  };
}

/** Client label for pickers. */
export function inventaireClientLabel(c) {
  return c?.name || c?.company_name || '';
}

/** Standard client select options for inventaire dialogs. */
export function buildInventaireClientSelectOptions(clients = []) {
  return clients.map((c) => ({
    value: c.id,
    label: inventaireClientLabel(c) || c.id,
  }));
}

/** Next entity reference (رمز) for inventaire CRUD pages. */
export function nextInventaireEntityRef(settings, prefixKey, fallback, records, getRef) {
  const prefix = getInventoryEntityPrefix(settings, prefixKey, fallback);
  return generateEntityReference(prefix, records, getRef);
}

/** Cap list rows to configured itemsPerPage (real-time from إعدادات المخزون). */
export function sliceInventaireListItems(items = [], itemsPerPage = 50) {
  const limit = Math.max(1, parseInt(itemsPerPage, 10) || 50);
  return items.slice(0, limit);
}

/** Header date-range filter — records without dates stay visible. */
export function inventaireRecordInDateRange(dateRange, ...dateValues) {
  if (!dateRange?.start && !dateRange?.end) return true;
  for (const raw of dateValues) {
    if (raw != null && raw !== '' && isDateInRange(raw, dateRange)) return true;
  }
  const hasAnyDate = dateValues.some((raw) => raw != null && raw !== '');
  return !hasAnyDate;
}

/** Live list display settings: defaultView, itemsPerPage, header date range. */
export function useInventaireListSettings(inventoryConfig) {
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [viewMode, setViewMode] = useState(() => inventoryConfig?.defaultView || 'table');

  useEffect(() => {
    setViewMode(inventoryConfig?.defaultView || 'table');
  }, [inventoryConfig?.defaultView]);

  return {
    dateRange,
    setDateRange,
    viewMode,
    setViewMode,
    itemsPerPage: inventoryConfig?.itemsPerPage ?? 50,
  };
}

/** Shown when a submodule is turned off in إعدادات المخزون. */
export function InventoryConfigNotice({ message }) {
  if (!message) return null;
  return (
    <div className="mb-4 rounded-2xl border border-amber-500/35 bg-amber-500/12 px-4 py-3 text-amber-100 text-sm text-right leading-relaxed">
      {message}
    </div>
  );
}

/** Map inventaire stat config to QawaemErpListPageShell stats (max 4). */
export function mapInventaireStatCards(entries = [], max = 4) {
  return sliceQawaemStats(entries, max).map((stat, i) => ({
    key: stat.key || stat.label,
    label: stat.label,
    value: stat.value,
    icon: stat.icon,
    variant: stat.variant || (i % 2 === 0 ? 'copper' : 'forest'),
  }));
}

/** Build column picker defs from { key, labelKey? } + label fn `p`. */
export function buildInventaireColumnDefs(columns, p) {
  return columns.map((col) => ({
    key: col.key,
    label: col.labelKey ? p(col.labelKey) : col.label,
    checked: col.checked !== false,
    required: col.required,
  }));
}

/** Initial visibility map for table columns (excludes actions). */
export function buildInventaireColumnVisibilityState(columns = []) {
  return Object.fromEntries(
    columns
      .filter((c) => c.key !== 'actions')
      .map((c) => [c.key, true]),
  );
}

/** Column picker defs bound to visibility state (for QawaemTableHeaderActionsCell). */
export function buildInventaireQawaemColumnDefs(columns, p, columnVisibility) {
  return buildInventaireColumnDefs(
    columns.map((col) => ({
      ...col,
      checked: col.key === 'actions' ? true : columnVisibility[col.key] !== false,
    })),
    p,
  );
}

export function isInventaireColumnVisible(columnVisibility, key) {
  return columnVisibility[key] !== false;
}

/** Checkbox column + visible data columns + actions column. */
export function inventaireTableColSpan(columnVisibility) {
  const visibleData = Object.values(columnVisibility).filter((v) => v !== false).length;
  return 1 + visibleData + 1;
}

/** Bulk-delete button for Qawaem title filter row (matches Products.js). */
export function buildInventaireBulkDeleteFilterAction({ count, onDelete, p }) {
  if (!count) return null;
  return (
    <button
      type="button"
      onClick={onDelete}
      className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
      title={p('delete_selection', { defaultValue: 'حذف التحديد' })}
    >
      <Trash2 size={16} />
      <span>{p('delete_count', { count })}</span>
    </button>
  );
}

/** Checkbox column header cell with indeterminate select-all. */
export function InventaireTableSelectAllHeader({ allRowsSelected, someRowsSelected, onSelectAll, p }) {
  return (
    <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
      <div className="flex justify-center">
        <TableCheckbox
          size="lg"
          checked={allRowsSelected}
          indeterminate={someRowsSelected}
          onChange={onSelectAll}
          title={allRowsSelected ? p('deselect_all', { defaultValue: 'إلغاء تحديد الكل' }) : p('select_all', { defaultValue: 'تحديد الكل' })}
        />
      </div>
    </th>
  );
}

/** Row checkbox cell. */
export function InventaireTableRowCheckbox({ checked, onChange }) {
  return (
    <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
      <div className="flex justify-center">
        <TableCheckbox checked={checked} onChange={onChange} />
      </div>
    </td>
  );
}

/** Standard actions column header className. */
export const INVENTAIRE_TABLE_ACTIONS_TH_CLASS =
  'px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle';

/** Secondary status/category filter for title row (الفترة · status · الأعمدة · search). */
export function buildInventaireSecondaryFilter({
  value,
  onChange,
  options,
  filterAllValue = 'all',
  filterAllLabel,
}) {
  const normalized = !value || value === filterAllValue ? filterAllValue : value;
  const current = options.find((o) => o.value === normalized);
  return {
    label: current?.label || filterAllLabel,
    value: normalized,
    options,
    onChange: (val) => onChange(val === filterAllValue ? filterAllValue : val),
    icon: Tag,
  };
}

/** Standard import/export toolbar props for inventaire list pages. */
export function buildInventaireImportExportProps({
  showImportDropdown,
  setShowImportDropdown,
  showExportDropdown,
  setShowExportDropdown,
  importDropdownRef,
  exportDropdownRef,
  importLabel,
  exportLabel,
  generatingLabel,
  isGeneratingPDF = false,
  handlers = {},
}) {
  const noop = () => {};
  return {
    importOpen: showImportDropdown,
    exportOpen: showExportDropdown,
    onToggleImport: () => {
      setShowImportDropdown(!showImportDropdown);
      setShowExportDropdown(false);
    },
    onToggleExport: () => {
      setShowExportDropdown(!showExportDropdown);
      setShowImportDropdown(false);
    },
    importRef: importDropdownRef,
    exportRef: exportDropdownRef,
    importLabel,
    exportLabel,
    generatingLabel,
    isGeneratingPDF,
    onImportCSV: handlers.onImportCSV || handlers.onImport || noop,
    onImportExcel: handlers.onImportExcel || handlers.onImport || noop,
    onImportJSON: handlers.onImportJSON || noop,
    onExportPDF: handlers.onExportPDF || noop,
    onExportExcel: handlers.onExportExcel || noop,
    onExportCSV: handlers.onExportCSV || handlers.onExport || noop,
    onExportJSON: handlers.onExportJSON || noop,
    showJsonImportExport: handlers.showJsonImportExport ?? false,
  };
}
